/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { allLegalPolicies } from 'content-collections'
import { LEGAL_MIN_ACCEPTANCE_DISABLED } from './policy-version-constants'

const LEGAL_POLICY_DOC_PATHS = {
  termsOfUse: 'legal/terms-of-use',
  acceptableUsePolicy: 'legal/acceptable-use-policy',
} as const

export type LegalPolicyId = keyof typeof LEGAL_POLICY_DOC_PATHS

export type LegalPolicyAcceptedVersions = {
  termsOfUseAcceptedVersion?: string | null
  acceptableUsePolicyAcceptedVersion?: string | null
}

function legalPolicyDoc(policyId: LegalPolicyId) {
  const path = LEGAL_POLICY_DOC_PATHS[policyId]
  const doc = allLegalPolicies.find((policy) => policy.path === path)
  if (!doc?.policyVersion) {
    throw new Error(`Missing policy_version frontmatter for ${path}.md`)
  }
  if (!doc.minAcceptanceVersion) {
    throw new Error(`Missing min_acceptance_version frontmatter for ${path}.md`)
  }
  return doc
}

function policyVersionFor(policyId: LegalPolicyId): string {
  return legalPolicyDoc(policyId).policyVersion
}

function minAcceptanceVersionFor(policyId: LegalPolicyId): string {
  return legalPolicyDoc(policyId).minAcceptanceVersion
}

/**
 * Current version of each legal policy recorded at signup / re-acceptance.
 * Sourced from `policy_version` in the help-center markdown (content-collections build).
 * Bump the frontmatter on only the document that changed.
 */
export const CURRENT_LEGAL_POLICY_VERSIONS = {
  termsOfUse: policyVersionFor('termsOfUse'),
  acceptableUsePolicy: policyVersionFor('acceptableUsePolicy'),
}

/**
 * Minimum accepted version required to use the Service.
 * Sourced from `min_acceptance_version`. Use `DISABLE` to skip re-acceptance.
 * Set to a YYYY-MM-DD date only for material changes that require users to re-accept.
 */
export const REQUIRED_LEGAL_POLICY_VERSIONS = {
  termsOfUse: minAcceptanceVersionFor('termsOfUse'),
  acceptableUsePolicy: minAcceptanceVersionFor('acceptableUsePolicy'),
}

/** Signup must submit the exact published `policy_version`. */
export function isCurrentLegalPolicyVersions(versions: Record<string, unknown>): boolean {
  return (
    versions.termsOfUseAcceptedVersion === CURRENT_LEGAL_POLICY_VERSIONS.termsOfUse &&
    versions.acceptableUsePolicyAcceptedVersion === CURRENT_LEGAL_POLICY_VERSIONS.acceptableUsePolicy
  )
}

export function isMinAcceptanceDisabled(minAcceptanceVersion: string): boolean {
  return minAcceptanceVersion === LEGAL_MIN_ACCEPTANCE_DISABLED
}

/**
 * YYYY-MM-DD string compare: accepted version must be present and at or after the floor.
 * `DISABLE` skips the floor for every stored version, including null or older dates.
 */
export function isAcceptedVersionAtOrAboveFloor(
  acceptedVersion: string | null | undefined,
  minAcceptanceVersion: string
): boolean {
  if (isMinAcceptanceDisabled(minAcceptanceVersion)) {
    return true
  }
  if (!acceptedVersion) {
    return false
  }
  return acceptedVersion >= minAcceptanceVersion
}

/** Returning users need both policies at or above `min_acceptance_version`. */
export function userHasAcceptedRequiredLegalPolicies(versions: LegalPolicyAcceptedVersions): boolean {
  return (
    isAcceptedVersionAtOrAboveFloor(versions.termsOfUseAcceptedVersion, REQUIRED_LEGAL_POLICY_VERSIONS.termsOfUse) &&
    isAcceptedVersionAtOrAboveFloor(
      versions.acceptableUsePolicyAcceptedVersion,
      REQUIRED_LEGAL_POLICY_VERSIONS.acceptableUsePolicy
    )
  )
}
